import { withDefault, APP_NAME, APP_VERSION } from '../../core/util';
import {
  applicationStatusTypes,
  accountTypes,
  setTaxIDType,
  parsePhone,
  setRJCustomerPhones,
  getMailingAddressDifferentFlag
} from '../shared/utils';
import { PartialStateStore } from '../../core/types/application-state';
import {
  appAccountsToProfileAccounts,
  appInvestmentsToProfileInvestments,
  convertToChosenInvestments,
  getResidentialAddressForEnabled,
  filterMailingAddressForEnabled,
  setInvestmentFundName
} from '../shared/services/util';
import { DeepPartialProfileArgs } from '../../core/types/return-journey-profile';

export enum ContributoryIRAAccountTypes {
  TRADITIONAL = 'TRADITIONAL',
  ROTH = 'ROTH'
}

export const getIRAAccountType: any = IRAAccountType => {
  const type = {
    [ContributoryIRAAccountTypes.ROTH]: accountTypes.ROTH,
    [ContributoryIRAAccountTypes.TRADITIONAL]: accountTypes.TRADITIONAL
  }[IRAAccountType];
  return type;
};

export const setMaxedContributoryLimit = (
  loadedData,
  updateStateVar: Function
): boolean =>
  updateStateVar(loadedData?.contributionLimits?.alreadyReachedLimit);

// a catch-all to use when account type is not specified
export const convertAppDataToProfileDataContributory = (
  store: PartialStateStore
): DeepPartialProfileArgs => {
  const { app, chosenInvestments, progressStepText } = store;

  // add fund name to investment allocations; needed for form request
  const updatedInvestmentAllocations = setInvestmentFundName(
    chosenInvestments,
    app?.newInvestments
  );

  const newInvestments = appInvestmentsToProfileInvestments(
    updatedInvestmentAllocations
  );

  const existingAccounts = appAccountsToProfileAccounts(app?.existingAccounts);

  // TODO [2020-05-25]: Let's make this a util to combine new + existing investments for profile service
  const investmentAllocations = [...existingAccounts, ...newInvestments];

  return {
    applicationID: withDefault(app?.profileApplicationID, ''),
    emailAddress: app?.customer?.primaryEmailAddress,
    applicationStatus: withDefault(
      app?.applicationStatus,
      applicationStatusTypes.IN_PROGRESS
    ),
    investmentAllocations,
    // ToDo: Below types need to be added and pulled in from store w.r.t current flow
    accountType: withDefault(store?.accountType, accountTypes.CONTRIBUTORY),
    fundingType: app?.fundingMethodType,
    toIRAAccountType: app?.iraAccountType,
    fromIRAAccountType: app?.iraAccountType,
    investAmount: withDefault(store?.amount, 0),
    investAmountPreviousYear: withDefault(store?.priorYearAmount, 0),
    investAmountCurrentYear: withDefault(store?.currentYearAmount, 0),
    firstName: app?.customer?.name?.firstName,
    lastName: app?.customer?.name?.lastName,
    middleName: withDefault(app?.customer?.name?.middleName, ''),
    residentialAddress: {
      street1: app?.customer?.residentialAddress?.street1,
      street2: app?.customer?.residentialAddress?.street2,
      state: app?.customer?.residentialAddress?.state,
      zip: app?.customer?.residentialAddress?.zip,
      city: app?.customer?.residentialAddress?.city
    },
    mailingAddress: {
      street1: app?.customer?.mailingAddress?.street1,
      street2: app?.customer?.mailingAddress?.street2,
      state: app?.customer?.mailingAddress?.state,
      zip: app?.customer?.mailingAddress?.zip,
      city: app?.customer?.mailingAddress?.city
    },
    citizenship: withDefault(app?.customer?.citizenship, 'UNKNOWN'),
    dateOfBirth: app?.customer?.dob,
    taxID: withDefault(app?.customer?.taxID, ''),
    phoneNumbers:
      app?.customer?.phones?.map(({ areaCode, exchange, lastFour, type }) => ({
        phoneNumber: `(${areaCode}) ${exchange}-${lastFour}`,
        phoneNumberType: type
      })) || [],
    investmentSelectionPath: app?.investmentMethod,
    residentialAddressDifferentFromMailing: withDefault(
      store?.mailingAddressDifferent,
      false
    ),
    termsIDs: withDefault(
      app?.termsIDs?.map(id => id?.toString()),
      []
    ),
    consumerVersion: APP_VERSION,
    consumerName: withDefault(store?.consumerName, APP_NAME),
    progressStepText,
    progressStep: withDefault(store?.progressStep, 0)
  };
};

// below function to convert data back to store for RJ
export const convertProfileDataToAppDataContributory = async (
  profile: DeepPartialProfileArgs,
  store: PartialStateStore
): Promise<PartialStateStore> => {
  // build customer data for enabled users off customer endpoint data received (helps with prefilling)
  // exception is taxID/citiizenship data, which must be pulled from journey profile data
  // authenticated and new user will build customer data from journey profile
  const { app, enabled } = store;
  const phones = setRJCustomerPhones(
    profile?.phoneNumbers,
    app?.customer?.phones
  );

  const customerData = enabled
    ? {
        ...app?.customer,
        citizenship: profile?.citizenship,
        taxIDType: setTaxIDType(profile?.citizenship),
        residentialAddress: getResidentialAddressForEnabled(
          app?.customer?.residentialAddress,
          profile?.residentialAddress
        ),
        // pull email, phone and mailing address from profile service to
        // account for RPS only users submitting via FONASU RJ
        // since the customer call while doing RJ is not pulling them currently
        mailingAddress: withDefault(
          profile?.mailingAddress,
          filterMailingAddressForEnabled(app?.customer?.mailingAddress)
        ),
        primaryEmailAddress: withDefault(
          profile?.emailAddress,
          app?.customer?.primaryEmailAddress
        ),
        phones
      }
    : {
        name: {
          firstName: profile?.firstName,
          middleName: withDefault(profile?.middleName, ''),
          lastName: profile?.lastName
        },
        citizenship: profile?.citizenship,
        dob: profile?.dateOfBirth,
        taxID: profile?.taxID,
        taxIDType: setTaxIDType(profile?.citizenship),
        residentialAddress: {
          street1: profile?.residentialAddress?.street1,
          street2: profile?.residentialAddress?.street2,
          state: profile?.residentialAddress?.state,
          zip: profile?.residentialAddress?.zip,
          city: profile?.residentialAddress?.city
        },
        mailingAddress: {
          street1: profile?.mailingAddress?.street1,
          street2: profile?.mailingAddress?.street2,
          state: profile?.mailingAddress?.state,
          zip: profile?.mailingAddress?.zip,
          city: profile?.mailingAddress?.city
        },
        primaryEmailAddress: profile?.emailAddress,
        // [{phoneNumberType: string, phoneNumber: string}]
        // needs to be converted to ->
        // [{ areaCode: string, exchange: string, lastFour: string, type: string }]
        //
        // TODO [2020-06-20]: This is a big pain why can't backend use same format
        phones: profile?.phoneNumbers?.map(
          ({ phoneNumberType: type = '', phoneNumber = '' }) => ({
            ...parsePhone(phoneNumber),
            type
          })
        )
      };
  return {
    accountType: profile?.accountType,
    amount: profile?.investAmount,
    priorYearAmount: profile?.investAmountPreviousYear,
    currentYearAmount: profile?.investAmountCurrentYear,
    app: {
      customer: customerData,
      existingAccounts: [],
      newInvestments: profile?.investmentAllocations?.map(
        ({
          fundName,
          ticker,
          trpCode,
          fundingAmount,
          fundingAmountPercentage
        }) => ({
          fundName,
          ticker,
          trpCode,
          fundingDetails: {
            fundingAmount,
            fundingAmountPercentage
          }
        })
      ),
      investmentMethod: profile?.investmentSelectionPath,
      fundingMethodType: profile?.fundingType,
      profileApplicationID: profile?.applicationID,
      iraAccountType: profile?.toIRAAccountType,
      termsIDs: withDefault(
        profile?.termsIDs?.map(id => id?.toString()),
        []
      )
    },
    mailingAddressDifferent: getMailingAddressDifferentFlag(
      enabled,
      store,
      profile
    ),
    chosenInvestments: await convertToChosenInvestments(
      profile?.investmentAllocations,
      profile?.investmentSelectionPath,
      app?.customer?.dob,
      enabled
    ),
    progressStep: withDefault(profile?.progressStep, 0),
    progressStepText: profile?.progressStepText,
    consumerVersion: profile?.consumerVersion,
    consumerName: profile?.consumerName
  };
};
